import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import type {
  ArgusCollectionDraftV1,
  ArgusDiagnosisStatus,
  ArgusStaticIntakeV1,
  ArgusWorkflowId,
} from '../types';
import { buildArgusRunnerPath, getArgusWorkflowById, persistArgusWorkflow } from '../lib/workflow';
import { prepareGalleryStimuli } from '../lib/stimulusPreload';
import { preloadFaceAssets } from '../lib/face';
import { resetClientRunId } from '../lib/clientRun';
import { productTokens } from '../theme';

const MAIN_WORKFLOW_ID: ArgusWorkflowId = 'screening-v2';

interface TaskResult {
  task: string;
  label: string;
  confidence: number;
  prob?: number;
  threshold?: number;
}

interface DiagnosisResult {
  id?: number;
  name?: string;
  label?: string;
  confidence?: number | null;
  tasks?: TaskResult[];
  state?: string;
  route?: string;
}

interface RunnerDoneMessage {
  type: 'argus:test:done';
  result?: DiagnosisResult;
}

const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';

const logDebug = (message: string, payload?: unknown) => {
  if (!DEBUG_ENABLED) return;
  if (payload !== undefined) {
    console.debug(message, payload);
  } else {
    console.debug(message);
  }
};

const filterTasks = (tasks?: TaskResult[]): TaskResult[] | undefined =>
  Array.isArray(tasks)
    ? tasks.filter((task) => String(task.task).toUpperCase() !== 'CDR')
    : undefined;

const sanitizeDiagnosis = (value?: DiagnosisResult | null): DiagnosisResult | null => {
  if (!value) return null;
  const filteredTasks = filterTasks(value.tasks);
  return filteredTasks ? { ...value, tasks: filteredTasks } : { ...value, tasks: undefined };
};

export default function TestLauncher(): JSX.Element {
  const nav = useNavigate();
  const winRef = useRef<Window | null>(null);
  const closeAttempts = useRef(0);

  const [status, setStatus] = useState<'idle' | 'opened' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnosisResult | null>(null);
  const [stimulusWarmState, setStimulusWarmState] = useState<
    'idle' | 'warming' | 'ready' | 'error'
  >('idle');
  const [stimulusWarmError, setStimulusWarmError] = useState<string | null>(null);

  const [participantId, setParticipantId] = useState<string>('');
  const [officialDiagnosisStatus, setOfficialDiagnosisStatus] = useState<ArgusDiagnosisStatus | ''>(
    '',
  );
  const [age, setAge] = useState<number | ''>('');
  const [sex, setSex] = useState<'M' | 'F' | ''>('');
  const [education, setEducation] = useState<number | ''>('');

  const selectedWorkflow = useMemo(() => getArgusWorkflowById(MAIN_WORKFLOW_ID), []);

  useEffect(() => {
    persistArgusWorkflow(selectedWorkflow);
  }, [selectedWorkflow]);

  useEffect(() => {
    let active = true;
    setStimulusWarmState('warming');
    setStimulusWarmError(null);

    void preloadFaceAssets();

    prepareGalleryStimuli(selectedWorkflow.configPath)
      .then(() => {
        if (!active) return;
        setStimulusWarmState('ready');
      })
      .catch((error) => {
        if (!active) return;
        setStimulusWarmState('error');
        setStimulusWarmError(error instanceof Error ? error.message : '素材预载失败');
      });

    return () => {
      active = false;
    };
  }, [selectedWorkflow.configPath]);

  const persistResult = useCallback((value: DiagnosisResult | null) => {
    try {
      if (!value) {
        localStorage.removeItem('argus:last_result');
        return;
      }
      const sanitized = sanitizeDiagnosis(value);
      localStorage.setItem('argus:last_result', JSON.stringify(sanitized));
    } catch (persistErr) {
      logDebug('[Launcher] failed to persist result', persistErr);
    }
  }, []);

  const closePopup = useCallback((label: string) => {
    const handle = winRef.current;
    if (!handle || handle.closed) {
      return;
    }
    try {
      handle.close();
      logDebug(label);
    } catch (closeErr) {
      logDebug(`${label} (failed)`, closeErr);
    }
  }, []);

  const closePopupRef = useRef(closePopup);

  useEffect(() => {
    closePopupRef.current = closePopup;
  }, [closePopup]);

  const getStoredStaticName = useCallback((): string | undefined => {
    try {
      const collectionRaw = localStorage.getItem('argus_collection_context');
      if (collectionRaw) {
        const parsed = JSON.parse(collectionRaw) as ArgusCollectionDraftV1;
        const value = parsed?.participant?.name;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.length > 0) return trimmed;
        }
      }
      const raw = localStorage.getItem('argus_static');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return undefined;
      const value = (parsed as Record<string, unknown>)['name'];
      if (typeof value !== 'string') return undefined;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    } catch (readErr) {
      logDebug('[Launcher] failed to read stored name', readErr);
      return undefined;
    }
  }, []);

  useEffect(() => {
    try {
      const collectionRaw = localStorage.getItem('argus_collection_context');
      if (collectionRaw) {
        const parsed = JSON.parse(collectionRaw) as ArgusCollectionDraftV1;
        if (parsed?.version === 1) {
          if (typeof parsed.participant?.clinicParticipantId === 'string') {
            setParticipantId(parsed.participant.clinicParticipantId);
          }
          if (typeof parsed.officialDiagnosis?.status === 'string') {
            setOfficialDiagnosisStatus(parsed.officialDiagnosis.status);
          }
        }
      }

      const raw = localStorage.getItem('argus_static');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      const obj = parsed as Record<string, unknown>;
      const rawAge = obj['age'];
      if (typeof rawAge === 'number' && Number.isFinite(rawAge)) {
        setAge(rawAge);
      } else if (typeof rawAge === 'string' && rawAge.trim() !== '') {
        const parsedAge = Number(rawAge);
        if (Number.isFinite(parsedAge)) setAge(parsedAge);
      }
      const rawSex = obj['sex'];
      if (rawSex === 'M' || rawSex === 'F') setSex(rawSex);
      const rawEdu = obj['education'];
      if (typeof rawEdu === 'number' && Number.isFinite(rawEdu)) {
        setEducation(rawEdu);
      } else if (typeof rawEdu === 'string' && rawEdu.trim() !== '') {
        const parsedEdu = Number(rawEdu);
        if (Number.isFinite(parsedEdu)) setEducation(parsedEdu);
      }
    } catch (loadErr) {
      logDebug('[Launcher] failed to load cached static info', loadErr);
    }
  }, []);

  const completeRun = useCallback(
    (payload?: DiagnosisResult) => {
      setStatus('done');
      const fallbackName = getStoredStaticName();
      const merged = payload
        ? (() => {
            const resolvedName = payload.name ?? fallbackName;
            return resolvedName ? { ...payload, name: resolvedName } : payload;
          })()
        : undefined;
      const enriched = sanitizeDiagnosis(merged);
      if (enriched) {
        setResult(enriched);
        persistResult(enriched);
      } else {
        setResult(null);
        persistResult(null);
      }
      closePopupRef.current?.('[Launcher] closed popup after completion');
      setTimeout(() => {
        const targetPath = enriched?.route ?? (enriched?.id ? `/results/${enriched.id}` : '/dashboard');
        logDebug('[Launcher] navigate after completion', targetPath);
        nav(targetPath, { replace: true });
      }, 200);
    },
    [getStoredStaticName, nav, persistResult],
  );

  useEffect(() => {
    localStorage.removeItem('argus:test:done');
    const channel = new BroadcastChannel('argus-runner');

    const onMessage = (event: MessageEvent<RunnerDoneMessage>) => {
      logDebug('[Launcher] broadcast message', event.data);
      if (event.data?.type === 'argus:test:done') {
        completeRun(event.data.result ?? undefined);
      }
    };

    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [completeRun]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'argus:test:done') {
        logDebug('[Launcher] storage completion event');
        try {
          const raw = localStorage.getItem('argus:last_result');
          const parsed = raw ? (JSON.parse(raw) as DiagnosisResult) : null;
          completeRun(sanitizeDiagnosis(parsed) ?? undefined);
        } catch (parseErr) {
          logDebug('[Launcher] failed to read stored result', parseErr);
          completeRun();
        }
      }

      if (event.key === 'argus:please-close') {
        closeAttempts.current = 0;
        const retryClose = () => {
          closeAttempts.current += 1;
          closePopupRef.current?.(
            `[Launcher] storage retry close attempt ${closeAttempts.current}`,
          );
          if (winRef.current && !winRef.current.closed && closeAttempts.current < 15) {
            window.setTimeout(retryClose, 100);
          }
        };
        retryClose();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [closePopup, completeRun]);

  const prepareRunnerContext = useCallback((): string | null => {
    setError(null);
    setStatus('idle');
    const workflow = selectedWorkflow;

    const trimmedParticipantId = participantId.trim();
    if (!trimmedParticipantId) {
      setError('请输入院内受试者编号。');
      return null;
    }
    if (!officialDiagnosisStatus) {
      setError('请选择参考认知状态。');
      return null;
    }
    if (typeof age !== 'number' || !Number.isFinite(age) || age <= 0) {
      setError('请输入有效年龄。');
      return null;
    }
    if (typeof education !== 'number' || !Number.isFinite(education) || education <= 0) {
      setError('请选择受教育程度。');
      return null;
    }
    if (sex !== 'M' && sex !== 'F') {
      setError('请选择性别。');
      return null;
    }

    try {
      const intake: ArgusStaticIntakeV1 = {
        version: 1,
        age,
        sex,
        education,
      };
      const browserNavigator = navigator as Navigator & { deviceMemory?: number };
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const clientRunId = resetClientRunId();
      const collectionContext: ArgusCollectionDraftV1 = {
        version: 1,
        participant: {
          clinicParticipantId: trimmedParticipantId,
        },
        workflow: {
          workflowId: workflow.workflowId,
          protocolId: workflow.protocolId,
          runnerMode: workflow.runnerMode,
          label: workflow.label,
        },
        officialDiagnosis: {
          status: officialDiagnosisStatus as ArgusDiagnosisStatus,
        },
        intake,
        collectionMeta: {
          clientRunId,
          launchedAt: new Date().toISOString(),
          launcherOrigin: window.location.origin,
          launcherPath: window.location.pathname,
          ...(timeZone ? { timeZone } : {}),
          ...(navigator.language ? { locale: navigator.language } : {}),
          ...(navigator.userAgent ? { userAgent: navigator.userAgent } : {}),
          ...(browserNavigator.platform ? { platform: browserNavigator.platform } : {}),
          ...(window.innerWidth > 0 ? { viewportWidth: window.innerWidth } : {}),
          ...(window.innerHeight > 0 ? { viewportHeight: window.innerHeight } : {}),
          ...(window.screen.width > 0 ? { screenWidth: window.screen.width } : {}),
          ...(window.screen.height > 0 ? { screenHeight: window.screen.height } : {}),
          ...(window.devicePixelRatio > 0 ? { pixelRatio: window.devicePixelRatio } : {}),
          ...(navigator.hardwareConcurrency > 0
            ? { hardwareConcurrency: navigator.hardwareConcurrency }
            : {}),
          ...(typeof browserNavigator.deviceMemory === 'number' && browserNavigator.deviceMemory > 0
            ? { deviceMemoryGb: browserNavigator.deviceMemory }
            : {}),
        },
      };
      persistArgusWorkflow(workflow);
      localStorage.setItem('argus_static', JSON.stringify(intake));
      localStorage.setItem('argus_collection_context', JSON.stringify(collectionContext));
      localStorage.removeItem('argus_formDataId');
    } catch (persistErr) {
      logDebug('[Launcher] failed to persist static payload', persistErr);
      setError('无法保存本次测试信息，请检查浏览器隐私/存储设置后重试。');
      return null;
    }

    return `${window.location.origin}${buildArgusRunnerPath('/runner/start', workflow.workflowId)}`;
  }, [age, education, officialDiagnosisStatus, participantId, sex, selectedWorkflow]);

  const openRunner = useCallback(() => {
    const url = prepareRunnerContext();
    if (!url) return;
    const windowFeatures =
      'popup=yes,width=1280,height=800,toolbar=0,menubar=0,location=0,status=0,noopener=no,noreferrer=no';

    const handle = window.open(url, 'ArgusTestRunner', windowFeatures);
    if (!handle) {
      setStatus('opened');
      window.location.assign(url);
      return;
    }

    winRef.current = handle;
    setStatus('opened');
  }, [prepareRunnerContext]);

  const renderResult = () => {
    if (!result) return null;
    const targetPath = result.id ? `/results/${result.id}` : null;
    return (
      <>
        <Typography variant="h6" sx={{ mt: 3 }}>
          结果
        </Typography>
        {result.name && <Typography>姓名：{result.name}</Typography>}
        {result.label ? <Typography>标签：{result.label}</Typography> : null}
        {typeof result.confidence === 'number' ? (
          <Typography>置信度：{result.confidence.toFixed(2)}</Typography>
        ) : (
          <Typography>状态：采集完成</Typography>
        )}
        {Array.isArray(result.tasks) && result.tasks.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              分项结果
            </Typography>
            <ul style={{ marginTop: 4 }}>
              {result.tasks.map((task) => (
                <li key={task.task}>
                  <Typography variant="body2">
                    {task.task}：{task.label}（{task.confidence.toFixed(2)}）
                    {typeof task.prob === 'number' ? ` • 风险 ${task.prob.toFixed(2)}` : ''}
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        )}
        <Box display="flex" gap={2} sx={{ mt: 2 }}>
          {targetPath ? (
            <Button variant="contained" onClick={() => nav(targetPath, { replace: true })}>
              查看详情
            </Button>
          ) : (
            <Button variant="contained" onClick={() => nav('/dashboard', { replace: true })}>
              返回控制台
            </Button>
          )}
          <Button variant="outlined" onClick={() => setResult(null)}>
            隐藏结果
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: '100%',
        minHeight: 0,
        py: { xs: 2, sm: 2.5 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader
        title="开始新测试"
        subtitle="填写受试者编号、参考认知状态和基础信息后进入筛查流程"
        action={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ justifyContent: 'flex-end' }}
          >
            {status === 'opened' ? (
              <Button variant="outlined" size="large" onClick={() => winRef.current?.focus()}>
                置于前台
              </Button>
            ) : null}
            <Button variant="contained" size="large" onClick={openRunner}>
              打开筛查流程
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ width: '100%', maxWidth: 760, mx: 'auto', flex: '1 1 auto', minHeight: 0 }}>
        <CardContent
          sx={{
            p: { xs: 2.5, sm: 3 },
            height: '100%',
            overflow: 'auto',
            '&:last-child': { pb: { xs: 2.5, sm: 3 } },
          }}
        >
          <Typography variant="h6" color="primary.dark" gutterBottom>
            筛查流程
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            当前默认入口为筛查采集流程。它会保存完整视频、刺激时间线与即时筛查摘要，作为标准测试电池。
          </Typography>
          {stimulusWarmState === 'ready' ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              筛查流程素材已预载完成；打开窗口后即可热启动进入测试。
            </Alert>
          ) : stimulusWarmState === 'warming' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              正在后台预载筛查流程素材；你可以继续填写表单并先进入测试窗口，若尚未完成会在最终开始前短暂等待。
            </Alert>
          ) : stimulusWarmState === 'error' ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {stimulusWarmError ?? '筛查流程素材预载失败。'} 测试窗口会再尝试一次预载。
            </Alert>
          ) : null}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: alpha(productTokens.argus, 0.045),
              border: `1px solid ${alpha(productTokens.argus, 0.12)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {selectedWorkflow.shortLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {selectedWorkflow.timingLabel}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {selectedWorkflow.summary}
            </Typography>
          </Box>

          <Typography variant="h6" color="primary.dark" gutterBottom>
            受试者基本信息
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            以下信息会与完整视频、刺激时间线一起保存，用于后续分析与结果整理。
            采集时间、站点来源与设备环境会自动记录，无需手填。
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="院内受试者编号"
              value={participantId}
              onChange={(event) => setParticipantId(event.target.value.trimStart())}
              required
            />
            <FormControl fullWidth required>
              <InputLabel id="official-diagnosis-label">参考认知状态</InputLabel>
              <Select
                labelId="official-diagnosis-label"
                label="参考认知状态"
                value={officialDiagnosisStatus}
                onChange={(event) =>
                  setOfficialDiagnosisStatus(event.target.value as ArgusDiagnosisStatus)
                }
              >
                <MenuItem value="dementia">痴呆</MenuItem>
                <MenuItem value="mci">轻度认知障碍（MCI）</MenuItem>
                <MenuItem value="cognitively_normal">认知正常</MenuItem>
                <MenuItem value="subjective_cognitive_decline">主观认知下降</MenuItem>
                <MenuItem value="not_yet_confirmed">暂未确认</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="年龄（岁）"
              type="number"
              inputProps={{ min: 18, max: 120 }}
              value={age}
              onChange={(event) =>
                setAge(event.target.value === '' ? '' : Number(event.target.value))
              }
              required
            />
            <FormControl fullWidth>
              <InputLabel id="edu-label">文化程度</InputLabel>
              <Select
                labelId="edu-label"
                label="文化程度"
                value={education}
                onChange={(event) => setEducation(Number(event.target.value))}
                required
              >
                <MenuItem value={1}>1 不识字</MenuItem>
                <MenuItem value={2}>2 小学</MenuItem>
                <MenuItem value={3}>3 初中</MenuItem>
                <MenuItem value={4}>4 高中或技校</MenuItem>
                <MenuItem value={5}>5 大专</MenuItem>
                <MenuItem value={6}>6 大学</MenuItem>
                <MenuItem value={7}>7 研究生（包括硕士或博士）</MenuItem>
              </Select>
            </FormControl>
            <FormControl required>
              <InputLabel id="sex-label">性别</InputLabel>
              <Select
                labelId="sex-label"
                label="性别"
                value={sex}
                onChange={(event) => setSex(event.target.value as 'M' | 'F' | '')}
              >
                <MenuItem value="">
                  <em>请选择</em>
                </MenuItem>
                <MenuItem value="F">女</MenuItem>
                <MenuItem value="M">男</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" color="text.secondary">
            测试将在单独窗口中进行，以保持页面整洁。
            {' 运行期间会录制高压缩完整摄像头视频并写入采集记录。'}
          </Typography>

          {status === 'done' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              测试已完成——请查看下方结果。
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            无法打开窗口？{' '}
            <a
              href={buildArgusRunnerPath('/runner/start', selectedWorkflow.workflowId)}
              target="_blank"
              onClick={(event) => {
                const url = prepareRunnerContext();
                if (!url) {
                  event.preventDefault();
                  return;
                }
                event.currentTarget.href = url;
                setStatus('opened');
              }}
              rel="noreferrer"
            >
              点此打开
            </a>
            .
          </Typography>
          {renderResult()}
        </CardContent>
      </Card>
    </Container>
  );
}
